'use client';
import React, { createRef, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Script from 'next/script';

import useInterval from 'src/hooks/useInterval';

export default function YouTubeVideo({ autoPlay = true, source, onVideoEnded }) {
    const playerRef = useRef<any>(undefined);
    const containerRef = createRef<HTMLDivElement>();
    const [visible, setVisible] = useState(autoPlay);
    const [ready, setReady] = useState(false);

    const interval = useInterval((clear) => {
        if (window.YT?.loaded) {
            setReady(true);
            clear();
        }
    }, 10);

    useEffect(() => {
        if (!source || !ready || !visible) {
            return;
        }

        createVideo();

        return () => {
            destroyVideo();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source, visible, ready]);

    const createVideo = () => {
        const onPlayerReady = (event) => {
            event.target.playVideo();
        };

        const onPlayerStateChange = (event) => {
            if (event.data === window.YT?.PlayerState?.ENDED) {
                onVideoEnded?.();
                setVisible(false);
            }
        };

        if (containerRef.current) {
            const el = document.createElement('div');
            el.setAttribute('id', `player-${source}`);
            containerRef.current.appendChild(el);
        }

        playerRef.current = new window.YT.Player(`player-${source}`, {
            videoId: source,
            playerVars: {
                playsinline: 1,
                rel: 0,
                showinfo: 0,
                modestbranding: 1,
                autoplay: 1,
            },
            events: {
                onReady: onPlayerReady,
                onStateChange: onPlayerStateChange,
            },
        });
    };
    const destroyVideo = () => {
        if (playerRef.current) {
            playerRef.current.destroy();
            playerRef.current = null;
        }
    };

    const handlePlay = () => {
        setVisible(true);
    };

    return (
        <div ref={containerRef} className="aspect-w-16 aspect-h-9">
            {!visible && (
                <Image
                    src={`https://img.youtube.com/vi/${source}/maxresdefault.jpg`}
                    onClick={handlePlay}
                    alt="Play video"
                    fill
                />
            )}

            <Script src="//www.youtube.com/iframe_api" async defer />
        </div>
    );
}
